import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Channel, Device } from "../types";

interface DevicesState {
  devices: Device[];
  channelsByDevice: Record<number, Channel[]>;
  setDevices: (d: Device[]) => void;
  setChannels: (deviceId: number, ch: Channel[]) => void;
  setAllChannels: (byDevice: Record<number, Channel[]>) => void;
  patchDevice: (id: number, patch: Partial<Device>) => void;
  clear: () => void;
}

// Se persiste en localStorage para que al recargar (F5) la lista se muestre
// instantaneamente desde cache, mientras react-query refetchea en segundo
// plano y reemplaza la data por la fresca. Sin persistencia, el sidebar se
// queda con "No hay dispositivos" hasta que la query termina.
export const useDevicesStore = create<DevicesState>()(
  persist(
    (set, get) => ({
      devices: [],
      channelsByDevice: {},
      setDevices: (devices) => set({ devices }),
      setChannels: (deviceId, ch) =>
        set((s) => ({ channelsByDevice: { ...s.channelsByDevice, [deviceId]: ch } })),
      // Carga masiva (useDevices): UN solo set para todos los equipos. Con
      // persist, cada set reescribe localStorage completo (~1 MB): hacerlo
      // una vez por equipo (~356) congelaba la pantalla varios segundos.
      // Mismo resultado que llamar setChannels por cada equipo: mezcla sobre
      // lo que ya habia, no borra los equipos ausentes de la respuesta.
      setAllChannels: (byDevice) =>
        set((s) => ({ channelsByDevice: { ...s.channelsByDevice, ...byDevice } })),
      // El WS manda device.status de cada equipo online cada 120 s (refresco
      // de discos) aunque is_online no cambie. Con persist, cada set
      // reescribe localStorage completo (~1 MB) y crea un array devices nuevo
      // (re-render de arbol, grilla, banner...): si el equipo no esta o ya
      // tiene esos valores, no se llama a set (dentro de set persist escribe
      // igual, por eso se compara antes con get()).
      patchDevice: (id, patch) => {
        const keys = Object.keys(patch) as (keyof Device)[];
        const changes = get().devices.some(
          (d) => d.id === id && keys.some((k) => !Object.is(d[k], patch[k])),
        );
        if (!changes) return;
        set((s) => ({
          devices: s.devices.map((d) => (d.id === id ? { ...d, ...patch } : d)),
        }));
      },
      clear: () => set({ devices: [], channelsByDevice: {} }),
    }),
    { name: "vms-devices" },
  ),
);

// Selector helper: cantidad y proporcion de devices accesibles que estan
// reportados como offline. El banner del Layout lo usa para alertar al
// operador cuando hay degradacion en la conectividad (VPN flapping,
// monitor de health no llega a los NVRs, etc.) sin agregar polling extra.
export function getOfflineStats(devices: Device[]): { offline: number; total: number; ratio: number } {
  if (devices.length === 0) return { offline: 0, total: 0, ratio: 0 };
  const accessible = devices.filter((d) => d.is_enabled !== false);
  const offline = accessible.filter((d) => !d.is_online).length;
  const total = accessible.length;
  return { offline, total, ratio: total > 0 ? offline / total : 0 };
}
